import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import { SET_MY_TASK_NOTE } from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";

/**
 * TaskNoteModal — edits the signed-in user's private Tasks-console note.
 *
 * ─────────────────────────────────────────────────────────────────
 * NOT `UpdateNoteModal`. That one looks similar and is not reusable: it calls
 * `UPDATE_CLIENT_NOTE` directly and expects a client-note object with
 * `createdBy` / `updatedBy` / `dateCreated`. This note has none of that — it is
 * one string on one user, with no authorship to track because there is only
 * ever one author.
 *
 * The mutation takes NO userId. The server resolves off `context.user`, so
 * there is no id here to get wrong.
 */

const MAX_LEN = 2000;

const TaskNoteModal = ({ open, onClose, note, onSaved }) => {
  const { handleSnack } = useSnackbar();
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveNote] = useMutation(SET_MY_TASK_NOTE);

  /* Re-seeded on every open, not just on mount: the modal stays mounted by its
     host, so a stale draft would survive a cancel and reappear next time. */
  useEffect(() => {
    if (open) {
      setDraft(note || "");
      setError("");
    }
  }, [open, note]);

  const handleSave = async () => {
    if (draft.length > MAX_LEN) {
      setError(`Too long — ${draft.length} of ${MAX_LEN} characters.`);
      return;
    }

    setSaving(true);
    try {
      const { data } = await saveNote({ variables: { note: draft } });
      /* Hand back what the SERVER stored, not the draft: it trims, and it
         turns an empty string into null. Echoing the draft would leave the
         panel showing a value the database does not hold. */
      onSaved?.(data?.setMyTaskNote ?? null);
      handleSnack("Note saved", 2000);
      onClose();
    } catch (err) {
      setError(err?.message || "Could not save the note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <BdrModal
      open={open}
      onClose={onClose}
      variant="panel"
      size="sm"
      title="My Note"
      actions={
        <>
          <BdrButton onClick={onClose} disabled={saving}>
            Cancel
          </BdrButton>
          <BdrButton variant="confirm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </BdrButton>
        </>
      }
    >
      {error && (
        <p className="bdr-alert bdr-alert--danger bdr-alert--start" role="alert">
          {error}
        </p>
      )}

      <BdrInput
        label="Note"
        name="taskNote"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        multiline
        rows={6}
        placeholder="Anything you want to keep in front of you."
        helper={`Only you can see this. ${draft.length} / ${MAX_LEN}`}
      />
    </BdrModal>
  );
};

export default TaskNoteModal;
